// Client-side error reporting facade.
//
// The top-level ErrorBoundary reports unhandled render errors through
// reportError(); other call sites can adopt it as they need to. It is the
// SINGLE seam where an error monitor (Sentry, GlitchTip, Bugsnag, …) plugs in.
//
// Deliberately no vendor SDK dependency yet — the provider + DSN is an ops
// decision. When you make it, turning on real capture is a localized change
// here (init the SDK once, add one capture call below), NOT a hunt across call
// sites:
//
//   import * as Sentry from "@sentry/react";
//   Sentry.captureException(error, { extra: context });
//
// gated on e.g. `import.meta.env.VITE_SENTRY_DSN`.

export interface ErrorContext {
  // Where the error originated, e.g. "react-error-boundary", "api".
  source?: string;
  // React component stack from componentDidCatch's ErrorInfo, when available.
  componentStack?: string | null;
  // Route the error happened on. Auto-filled from the URL when omitted.
  route?: string;
  // Freeform triage data (request url/status, ids, …). Kept in its own bag so
  // typos on the named fields above are still caught by the type checker.
  extra?: Record<string, unknown>;
}

// Reports an error through the app's single monitoring seam. Never throws — a
// failure in the reporting path must not mask or escalate the original error.
export function reportError(error: unknown, context: ErrorContext = {}): void {
  try {
    const enriched: ErrorContext = {
      route: typeof window !== "undefined" ? window.location.pathname : undefined,
      ...context,
      source: context.source ?? "unknown",
    };
    // Structured console output today — visible in the browser and picked up by
    // any console-forwarding provider. A provider capture call goes right here.
    console.error(`[reportError] source=${enriched.source}`, error, enriched);
  } catch {
    // Intentionally empty. The reporter must never throw or it could mask the
    // very error it's reporting; if even console.error is broken there is no
    // safe channel left, so we swallow rather than re-report (which would
    // re-enter the same broken primitive and escape).
  }
}
